import {
  useState,
  useEffect,
  createContext,
  useContext,
  FC,
  ReactNode,
  useCallback,
} from "react";
import {
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import { auth, authReady, googleProvider } from "../firebaseConfig";
import { queryClient } from "../index";

interface AuthContextType {
  user: User | null;
  refreshUser: () => Promise<boolean>;
  logout: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
  signIn: ({
    method,
    email,
    password,
  }: {
    method: "email" | "google";
    email?: string;
    password?: string;
  }) => Promise<boolean>;
  isAuthLoading: boolean;
  isUserVerified: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  refreshUser: async () => {
    throw new Error("regreshUser must be used within an AuthProvider");
  },
  logout: async () => Promise.resolve(),
  signIn: async () => {
    throw new Error("signIn must be used within an AuthProvider");
  },
  getValidToken: async () => null,
  isAuthLoading: true,
  isUserVerified: false,
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isUserVerified, setIsUserVerified] = useState(false);

  const getValidToken = useCallback(async (): Promise<string | null> => {
    await authReady;

    let currentUser = auth.currentUser;
    if (!currentUser) {
      try {
        const { user: anonymousUser } = await signInAnonymously(auth);
        setUser(anonymousUser);
        currentUser = anonymousUser;
      } catch (error) {
        console.error("Anonymous sign-in failed", error);
        return null;
      }
    }

    try {
      return await currentUser.getIdToken(); // optionally pass true to force refresh
    } catch (error) {
      console.error("Failed to fetch token", error);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<boolean> => {
    await authReady;

    if (auth.currentUser) {
      await auth.currentUser.reload();
      setUser(auth.currentUser);
      const verified = auth.currentUser.emailVerified;
      setIsUserVerified(verified);
      return verified;
    } else {
      try {
        const { user: anonymousUser } = await signInAnonymously(auth);
        if (!anonymousUser) {
          setUser(null);
          setIsUserVerified(false);
          return false;
        }
        setUser(anonymousUser);
        setIsUserVerified(false);
        return false;
      } catch (error) {
        console.error("Error during anonymous sign-in:", error);
        return false;
      }
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsUserVerified(false);
      queryClient.clear();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, []);

  const signIn = useCallback(
    async ({
      method,
      email,
      password,
    }: {
      method: "email" | "google";
      email?: string;
      password?: string;
    }): Promise<boolean> => {
      try {
        // Ensure session is persisted across browser reloads
        await setPersistence(auth, browserLocalPersistence);

        switch (method) {
          case "email":
            if (!email || !password) {
              throw new Error(
                "Email and password must be provided for email sign-in."
              );
            }
            await signInWithEmailAndPassword(auth, email, password);
            break;

          case "google":
            await signInWithPopup(auth, googleProvider);
            break;

          default:
            throw new Error(`Unsupported sign-in method: ${method}`);
        }

        // Reload the user to get latest data (like emailVerified)
        await auth.currentUser?.reload();
        const verified = auth.currentUser?.emailVerified ?? false;
        setUser(auth.currentUser);
        setIsUserVerified(verified);

        return verified;
      } catch (error) {
        console.error("Sign-in failed:", error);
        throw error;
      }
    },
    []
  );

  useEffect(() => {
    let unsubscribe: () => void = () => null;

    const setup = async () => {
      await authReady;

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setUser(firebaseUser);
        setIsUserVerified(!!firebaseUser?.emailVerified);
        setIsAuthLoading(false);
      });
    };

    setup();

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        refreshUser,
        logout,
        getValidToken,
        isAuthLoading,
        isUserVerified,
        signIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
